import { useQuery } from '@tanstack/react-query'
import { CreditCard, Smartphone, Store, Wallet } from 'lucide-react'
import { driver, useBalance } from '@/api/driver'
import { useTopUpOptions } from '@/api/topup'
import type { WalletTransaction } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock, PageTitle, StatusPill } from '@/components/common'
import { Card } from '@/components/ui/card'
import { dateTime, npr } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { TopUpCard } from './TopUp'

export function WalletPage() {
  const { t } = useI18n()
  const q = useBalance()
  const activity = useQuery({ queryKey: ['activity'], queryFn: driver.activity })
  const online = useTopUpOptions()
  if (q.isLoading) return <LoadingBlock rows={5} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  const { wallets, rfid_cards: cards, cpo_adjustments: topups } = q.data!

  // Charging is billed in small steps, so one session has many deductions: fold them per session.
  const tx = [...(activity.data?.transactions ?? []), ...topups]
  const seen = new Set<number>()
  const unique = tx.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)))
  const bySession = new Map<number, WalletTransaction>()
  const rows: WalletTransaction[] = []
  for (const x of unique.sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))) {
    if (x.transaction_type === 'deduct' && x.session_id != null) {
      const g = bySession.get(x.session_id)
      if (g) {
        g.amount += x.amount
        continue
      }
      const copy = { ...x, description: t('Charging, session #{id}', { id: x.session_id }) }
      bySession.set(x.session_id, copy)
      rows.push(copy)
    } else rows.push(x)
  }

  return (
    <div>
      <PageTitle title={t('Wallet')} sub={t('Your balance with each operator, and with Saadhan for a super card.')} />
      {wallets.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {wallets.map((w) => (
            <Card key={w.id} className={cn('p-4', w.cpo_id == null && 'bg-ink text-white')}>
              <p className={cn('text-sm', w.cpo_id == null ? 'text-white/70' : 'text-text-2')}>{w.cpo_id == null ? t('Saadhan wallet (super card)') : w.cpo_name}</p>
              <p className="tabular mt-1 text-2xl font-bold">{npr(w.balance)}</p>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-4 text-sm text-text-2">{t('No wallet yet. It opens when an operator adds credit for you.')}</Card>
      )}

      <Card className="mt-4 p-4">
        <p className="font-semibold">{t('Add credit')}</p>
        <div className="mt-3 space-y-3 text-sm">
          <p className="flex gap-3"><Store className="size-5 shrink-0 text-text-2" />{t('Pay at the counter of any station. The staff add it to your wallet with that operator straight away.')}</p>
          {online.data ? null : <p className="flex gap-3 text-text-2"><Smartphone className="size-5 shrink-0" />{t('Paying online with eSewa, Khalti or Fonepay is coming soon.')}</p>}
        </div>
      </Card>
      <TopUpCard hasSuperCard={cards.some((c) => c.is_supercard && c.is_active && !c.is_blocked && !c.is_expired)} />

      <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Your cards')}</h2>
      {cards.length ? (
        <Card className="divide-y divide-border">
          {cards.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3">
              <CreditCard className="size-5 text-text-2" />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-sm font-medium">{c.card_number}</p>
                <p className="text-xs text-text-2">{c.is_supercard ? t('Saadhan super card') : t('Operator card')}{c.expires_at ? ` · ${t('expires {d}', { d: dateTime(c.expires_at) })}` : ''}</p>
              </div>
              {c.is_blocked ? <StatusPill tone="danger">{t('Blocked')}</StatusPill> : c.is_expired ? <StatusPill tone="warning">{t('Expired')}</StatusPill> : !c.is_active ? <StatusPill tone="neutral">{t('Inactive')}</StatusPill> : <StatusPill tone="success">{t('Active')}</StatusPill>}
            </div>
          ))}
        </Card>
      ) : (
        <Card className="p-4 text-sm text-text-2">{t('No card yet. Ask the staff at a station for one; you need it to charge.')}</Card>
      )}
      <p className="mt-2 text-xs text-text-2">{t('Lost a card? Call the station that gave it to you so they can block it.')}</p>

      <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Activity')}</h2>
      {activity.isLoading ? (
        <LoadingBlock rows={3} />
      ) : rows.length ? (
        <Card className="divide-y divide-border">
          {rows.slice(0, 50).map((x) => (
            <div key={x.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{x.transaction_type === 'add' ? t('Credit added') : x.transaction_type === 'refund' ? t('Refund') : x.description ?? t('Charging')}</p>
                <p className="text-xs text-text-2">{dateTime(x.created_at)}</p>
              </div>
              <p className={cn('tabular text-sm font-semibold', x.amount < 0 || x.transaction_type === 'deduct' ? 'text-text' : 'text-success')}>
                {x.amount < 0 || x.transaction_type === 'deduct' ? '−' : '+'}{npr(Math.abs(x.amount))}
              </p>
            </div>
          ))}
        </Card>
      ) : (
        <EmptyState icon={<Wallet />} title={t('No activity yet')} />
      )}
    </div>
  )
}
